import { isEmpty } from '@sgme/fp';
import type { PointOptionsObject, SeriesLineOptions, SeriesPolygonOptions } from 'highcharts';
import { includes } from 'lodash-es';
import type {
  IClientTargetMarket,
  ProductGovernanceClient,
  ProductGovernanceMap,
  Zone,
} from 'models/streaming/productGovernance';
import type { SgBsTheme } from 'state/userPreferences/userPreferences.model';
import type { MakeRequired } from 'types/utils';
import { toHighchartsTheme } from 'utils/highcharts';

export type MifidChartOptions = {
  legend: boolean;
  chart: boolean;
  clientStatus: boolean;
  kne?: boolean;
  vfm?: boolean;
  theme: SgBsTheme;
};
export type MifidWidgetProps = {
  container: string;
  options: MifidChartOptions;
  maps?: ProductGovernanceMap[];
  client?: ProductGovernanceClient;
  debug?: boolean;
};

type RegionName = {
  name: string;
  nonAdvisedName?: string;
  showInLegend: boolean;
  validProductTargetMarketAdvisedRegions?: string[];
  isAdvised: boolean;
};
type Maximum = {
  complexity: number;
  risk: number;
};
type Data = {
  series?: (SeriesPolygonOptions | SeriesLineOptions)[];
  maximum?: Maximum;
} & Partial<ProductGovernanceMap>;

const NOT_MATCHED = 'Not matched';
const MATCHED = 'Matched';
const PARTIALLY_MATCHED = 'Partially matched';

export class Mifid2 {
  // eslint-disable-line no-unused-vars

  /**
   * Constructor of the component.
   */
  private _regionNames: Record<string, RegionName> = {};
  private _hasNadvs = false;

  private data: Record<string, Data> = {};
  private currentMap?: Data;

  constructor(private configuration: MifidWidgetProps) {
    if (!window.Highcharts) {
      throw new Error('Highcharts should be loaded in order to use the MIFID 2 widget');
    }

    if (!configuration) {
      throw new Error('Configuration is required to use the MIFID 2 widget');
    }

    this.configuration = configuration;

    this.configuration.options = {
      ...configuration.options,
      kne: configuration.options.kne ?? true,
      vfm: configuration.options.vfm ?? true,
    };

    this.data = {};
    // Region names, in human format...
    this._regionNames = {
      SOPADV: {
        name: 'K&E - Advanced',
        nonAdvisedName: 'SOPNADV',
        showInLegend: !this.configuration.options || this.configuration.options.legend,
        validProductTargetMarketAdvisedRegions: ['SOPADV', 'EDCTADV', 'MRETADV'],
        isAdvised: true,
      },
      SOPNADV: {
        name: 'K&E - Advanced',
        showInLegend: false,
        isAdvised: false,
      },
      EDCTADV: {
        name: 'K&E - Informed',
        nonAdvisedName: 'EDCNADV',
        showInLegend: !this.configuration.options || this.configuration.options.legend,
        validProductTargetMarketAdvisedRegions: ['EDCTADV', 'MRETADV'],
        isAdvised: true,
      },
      EDCNADV: {
        name: 'K&E - Informed',
        showInLegend: false,
        isAdvised: false,
      },
      MRETADV: {
        name: 'K&E - Basic',
        nonAdvisedName: 'MRETNADV',
        showInLegend: !this.configuration.options || this.configuration.options.legend,
        validProductTargetMarketAdvisedRegions: ['MRETADV'],
        isAdvised: true,
      },
      MRETNADV: {
        name: 'K&E - Basic',
        showInLegend: false,
        isAdvised: false,
      },
    };

    // Generate the data from the input
    this._generateData();
    if (this.data[Object.keys(this.data)[0]]) {
      // SetCurrentMap in context
      this.currentMap = this.data[Object.keys(this.data)[0]];
    }
    // Generate the HTML elements
    this._generateHTML();

    if (this.data[Object.keys(this.data)[0]] && this.configuration.options.chart) {
      // Generate the chart
      this._generateChart(this.data[Object.keys(this.data)[0]]);
    }
  }

  _isChartDisplayed() {
    return this.configuration.options.kne && this.configuration.options.chart;
  }

  _isKnowledgeAndExperienceResultDisplayed() {
    return this.configuration.options.kne && this.configuration.maps;
  }

  _isNonAdvisedSalesEligibilityDisplayed() {
    return this.configuration.options.kne && this.configuration.maps;
  }

  _isProductTestingResultDisplayed() {
    return this.configuration.options.vfm;
  }

  /**
   * Generate the flag for the status.
   * @param name Name to display
   * @param value value of the flag. Can be `true`, `false`, `yes`, `no`, `partial`.
   * @returns {string}
   * @private
   */
  _generateStatusFlag(name: string, value: string) {
    const testValue = value?.toString().toLowerCase();

    switch (testValue) {
      case 'true':
        return `<span title="${MATCHED}">${name} <i class="icon icon-md text-success">check_circle</i></span>`;

      case 'partial':
        return `<span title="${PARTIALLY_MATCHED}">${name} <i class="icon icon-md text-warning">adjust</i></span>`;

      default:
        return `<span title="${NOT_MATCHED}">${name} <i class="icon icon-md text-danger">cancel</i></span>`;
    }
  }

  _generateNonAdvisedSalesEligibilityFlag(name: string, nonAdvisedSalesEligibility?: string, ptmNonAdvised?: string) {
    const testValue = nonAdvisedSalesEligibility?.toString().toLowerCase();
    if (testValue !== 'false') {
      return `<span title="${MATCHED}">${name} <i class="icon icon-md text-success">check_circle</i></span>`;
    } else if (
      (ptmNonAdvised === 'MRETNADV' || ptmNonAdvised === 'EDCNADV' || ptmNonAdvised === 'SOPNADV') &&
      !this._hasNadvs
    ) {
      return `<span title="${NOT_MATCHED}">${name} <i class="icon icon-md text-danger">help_outline</i></span>`;
    }

    return `<span title="${NOT_MATCHED}">${name} <i class="icon icon-md text-danger">close</i></span>`;
  }

  /** Adaptation of the generate status flag function with the new RED status **/
  _generateMatchingStatus(
    targetAdv: IClientTargetMarket | undefined,
    targetNAdv: IClientTargetMarket | undefined,
    zone: string,
  ) {
    let classes = '';
    let title = '';
    let name = this._regionNames[zone].name;
    let eligibleAdv;
    let nonEligibleAdv;
    let eligibleNAdv;
    let nonEligibleNAdv;
    let isProductTargetMarketAdvised = false;
    const findByCurrentMapId = (value: string) => value === this.currentMap?.id;
    if (targetNAdv) {
      name = this._regionNames[targetNAdv.name.toUpperCase()].name;
      eligibleNAdv = targetNAdv.clientEligibleMap.find(findByCurrentMapId);
      nonEligibleNAdv = targetNAdv.clientNonEligibleMap.find(findByCurrentMapId);
    }

    if (targetAdv) {
      name = this._regionNames[targetAdv.name.toUpperCase()].name;
      eligibleAdv = targetAdv.clientEligibleMap.find(findByCurrentMapId);
      nonEligibleAdv = targetAdv.clientNonEligibleMap.find(findByCurrentMapId);

      isProductTargetMarketAdvised = includes(
        this._regionNames[targetAdv.name.toUpperCase()].validProductTargetMarketAdvisedRegions,
        this.currentMap?.productTargetMarketAdvised,
      );
    }

    const isProductEligibilityAdvisedValue =
      this.currentMap &&
      this.currentMap.productEligibilityAdvisedValue &&
      this.currentMap.productEligibilityAdvisedValue !== 'false';

    const isProductEligibilityNonAdvisedValue =
      this.currentMap &&
      this.currentMap.productEligibilityNonAdvisedValue &&
      this.currentMap.productEligibilityNonAdvisedValue !== 'false';

    this._hasNadvs = this._hasNadvs || !!nonEligibleNAdv || !!eligibleNAdv; // used for non advised sales eligibility calculation

    if (!nonEligibleAdv && !eligibleAdv && !nonEligibleNAdv && !eligibleNAdv) {
      // NA
      return `<span title="${title}" style="line-height:25px">${name}</span>
              <span class="fa-stack">
                <i class="icon icon-md text-warning">help_outline</i>
              </span>`;
    } else if (
      isProductTargetMarketAdvised &&
      (isProductEligibilityAdvisedValue || isProductEligibilityNonAdvisedValue)
    ) {
      //
      classes = '<i class="icon icon-md text-success">check</i>';
      title = MATCHED;
    } else {
      classes = '<i class="icon icon-md text-danger">close</i>';
      title = NOT_MATCHED;
    }

    return `<span title="${title}" style="line-height:25px">${name} ${classes}</span>`;
  }

  /**
   * Generate the HTML elements around the chart.
   * @private
   */
  _generateHTML() {
    const hostContainer = document.getElementById(this.configuration.container);
    if (!hostContainer) {
      return;
    }
    const style = document.createElement('style');
    style.innerHTML = `
      .mifid2-commands { border-bottom: solid 1px #ccc; padding-bottom: 5px; margin-bottom: 10px; }
      .mifid2-commands a { color: black; }
      .mifid2-commands a.selected { background-color: #dddedf; color: black; }
      .mifid2-client-status-details > div { vertical-align: top; display: inline-block; width: 50%; }
      .mifid2-chart { border-bottom: solid 1px #ccc; }
      .warning-text { color: darkorange; font-style: italic; }
    `;
    document.body.appendChild(style);
    if (this._isChartDisplayed()) {
      // `<div class="commands"></div>` contains the links to change the map used on the chart...
      const mapSelector = document.createElement('div');
      mapSelector.classList.add('mifid2-commands');

      // Add buttons to change the map
      let first = true;

      const maps = this.configuration.maps || [];

      for (const map of maps) {
        const btn = document.createElement('a');
        btn.classList.add('btn', 'mifid-2-map-selection');
        if (first) {
          btn.classList.add('selected');
          first = false;
        }
        btn.innerHTML = map.name;
        btn.onclick = () => {
          if (this.configuration.options.chart) {
            this._generateChart(this.data[map.id]);
          }
          if (this.configuration.options.clientStatus) {
            const statusContainer = document.querySelector('.mifid2-client-status-container')!;
            statusContainer.innerHTML = this._generateClientStatus(map);
          }

          document.querySelectorAll('a.mifid-2-map-selection').forEach(link => link.classList.remove('selected'));

          btn.classList.add('selected');
        };
        mapSelector.appendChild(btn);
      }
      hostContainer.appendChild(mapSelector);

      // Create the `<div>` that will contain the chart...
      const chartContainer = document.createElement('div');
      chartContainer.id = `mifid2-container-${this.configuration.container}`;
      chartContainer.className = toHighchartsTheme(this.configuration.options.theme);
      hostContainer.appendChild(chartContainer);
    }

    // Add status information
    this._addStatusInformationTo(hostContainer);
  }

  _addStatusInformationTo(container: Element) {
    if (this.configuration.client && this.configuration.options.clientStatus) {
      const statusContainer = document.createElement('div');
      statusContainer.classList.add('mifid2-client-status-container');
      statusContainer.innerHTML = this._generateClientStatus();
      container.appendChild(statusContainer);
    }
  }

  /** simple check to verify if client is Advised **/
  _isClientAdvised(name: string) {
    return this._regionNames[name.toUpperCase()].isAdvised;
  }

  /**
   *
   * Generate the HTML element for client status
   * @private
   */
  _generateClientStatus(map?: Data) {
    const client = this.configuration.client;
    if (map !== undefined) {
      this.currentMap = map;
    }
    // List advised / non advised zones
    let advised = '';
    let statusDetails = '';
    const nonAdvisedSalesEligibilityValue = this.currentMap?.productEligibilityNonAdvisedValue;
    let nonAdvisedSalesEligibility = '';
    if (client?.clientTargetMarkets && client.clientTargetMarkets.length > 0) {
      Object.keys(this._regionNames).forEach(zone => {
        if (this._isClientAdvised(zone)) {
          const targetAdv = client.clientTargetMarkets.find(tm => tm.name.toUpperCase() === zone);
          const targetNAdv = client.clientTargetMarkets.find(
            tm => tm.name.toUpperCase() === this._regionNames[zone].nonAdvisedName,
          );

          const advisedTag = this._generateMatchingStatus(targetAdv, targetNAdv, zone);
          advised += `<li><i class="icon icon-md">chevron_right</i> ${advisedTag}</li>`;
        }
      });

      // Non Advised Sales Eligibility
      const ptmNonAdvised = this.currentMap && this.currentMap.productTargetMarketNonAdvised;

      nonAdvisedSalesEligibility = `<h5>${this._generateNonAdvisedSalesEligibilityFlag(
        'Non Advised Sales Eligibility',
        nonAdvisedSalesEligibilityValue,
        ptmNonAdvised,
      )}</h5>`;
    } else if (client?.clientTargetMarkets && client.clientTargetMarkets.length === 0) {
      statusDetails = `<p style="font-weight: bold;" class="warning-text">Please provide client type coverage (Retail, Pro, ECP) in Maestro</p>`;
    } else {
      statusDetails = `<p style="font-weight: bold;" class="warning-text">No matching result available in assessment. Please contact Astro Team for support</p>`;
    }
    // Check for "VFM" (Value For Money) in product
    let productVFM = '';
    if (client?.product) {
      const flag = this._generateStatusFlag('Product Testing result&nbsp;&nbsp;', client?.product?.vfm?.toString());
      productVFM = `<h5>${flag}</h5>`;
      if (client.product.vfm === false || client.product.vfm === 'false') {
        productVFM += `<strong><p class="warning-text">As per Value For Money test, product doesn't outperform
      theoritical next best alternative in numerous occasions. Please contact your COO for further information</p><strong>`;
      }
    }

    const knowledgeAndExperienceResult = `
      <h5>Knowledge and experience result</h5>
      <div class="mifid2-client-status-details">
        <div>
          <ul class="fa-ul">${advised}</ul>
        </div>
        ${statusDetails}
      </div>
    `;
    return `
      <div class="mifid2-client-status">
        ${this._isKnowledgeAndExperienceResultDisplayed() ? knowledgeAndExperienceResult : ''}
        ${this._isNonAdvisedSalesEligibilityDisplayed() ? nonAdvisedSalesEligibility : ''}
        ${this._isProductTestingResultDisplayed() ? productVFM : ''}
      </div>
      <br>
    `;
  }

  /**
   * Get the risk for a given complexity on a given zone. Used to generate the "client zone" shape.
   * @param cplx the current complexity.
   * @param x1 the complexity of the zone.
   * @param x2 the complexity at risk 0 of the zone.
   * @param y the risk of the zone.
   * @returns {*}
   * @private
   */
  _riskForComplexity(cplx: number, x1: number, x2: number, y: number) {
    if (cplx <= x1) {
      return y;
    }
    if (cplx < x2) {
      return y - (y / (x2 - x1)) * (cplx - x1); // eslint-disable-line no-mixed-operators
    }
    return 0;
  }

  /**
   * Generate the Client Zone shape used in the chart.
   * @param zones
   * @returns
   * @private
   */
  _generateClientZone(zones: Zone[]) {
    const clientZone: SeriesLineOptions = {
      name: 'Non Advised / Advised limit',
      type: 'line',
      dashStyle: 'Dot',
      lineWidth: 2,

      marker: {
        enabled: true,
        radius: 5,
        symbol: 'circle',
        lineWidth: 0,
      },
      showInLegend: this.configuration.options.legend,
      data: [],
    };

    if (isEmpty(zones)) {
      return clientZone;
    }
    const data: PointOptionsObject[] = [];
    // We will go from 0 to 100 and for each element, we calculate the maximum risk for all zones.
    // let x =  zones[0].complexityRisk0 - 0.2 => Hack made on Po's demand to have only the
    // vertical line for client zone
    for (let x = zones[0].complexityRisk0 - 0.2; x <= 100; x += 0.2) {
      let y = 0;
      for (const zone of zones) {
        const y2 = this._riskForComplexity(x, zone.complexity, zone.complexityRisk0, zone.risk);
        y = Math.max(y, y2);
      }
      data.push([x, y]);

      if (y === 0) {
        break;
      }
    }

    clientZone.data = data.concat(data.slice().reverse());
    return clientZone;
  }

  /**
   * Generate the series for one map.
   * @param map The current map.
   * @param client The client information.
   * @returns
   * @private
   */
  _generateSeries(map: ProductGovernanceMap, client?: ProductGovernanceClient) {
    if (!map || !map.zones) {
      return;
    }
    const data: MakeRequired<Data, 'maximum' | 'series'> = {
      maximum: {
        complexity: 0,
        risk: 0,
      },
      series: [],
    };
    const zones: Zone[] = [];
    for (const zone in this._regionNames) {
      if (this._regionNames.hasOwnProperty(zone)) {
        const mapZoneKey = Object.keys(map.zones).find(key => key.toUpperCase() === zone.toUpperCase())!;
        if (map.zones.hasOwnProperty(mapZoneKey)) {
          let x1 = map.zones[mapZoneKey].complexity;
          let x2 = map.zones[mapZoneKey].complexityRisk0;
          let y = map.zones[mapZoneKey].risk;

          // Manage client tolerance - if specified for this zone
          if (client?.tolerances) {
            const toleranceKey = Object.keys(client.tolerances).find(key => key.toUpperCase() === zone.toUpperCase())!;
            if (client.tolerances[toleranceKey]) {
              const complexity = client.tolerances[toleranceKey].complexity;
              const risk = client.tolerances[toleranceKey].risk;
              x1 += complexity;
              x2 += complexity;
              y += risk;

              zones.push({
                complexity: x1,
                complexityRisk0: x2,
                risk: y,
              });
            }
          }

          data.maximum.complexity = Math.max(data.maximum.complexity, x1, x2);
          data.maximum.risk = Math.max(data.maximum.risk, y);

          const serie: SeriesPolygonOptions = {
            name: this._regionNames[zone].name,
            type: 'polygon',
            showInLegend: this._regionNames[zone].showInLegend,
            events: {
              legendItemClick: function () {
                return false;
              },
            },
            allowPointSelect: false,
            data: [
              [0, 0],
              [0, y],
              [x1, y],
              [x2, 0],
            ],
          };
          data.series.push(serie);
        }
      }
    }

    // Construct client products
    if (client) {
      //check if client complexity is out of graph bounds
      if (client.product.complexity > data.maximum.complexity) {
        data.maximum.complexity = client.product.complexity + 5;
      }

      //check if client risk is out of graph bounds
      if (client.product.risk > data.maximum.risk) {
        data.maximum.risk = client.product.risk + 5;
      }

      const product: SeriesPolygonOptions = {
        name: 'Product',
        type: 'polygon',
        // fillColor: false,
        showInLegend: this.configuration.options.legend,
        data: [[client.product.complexity, client.product.risk]],
        marker: {
          enabled: true,
          radius: 5,
          symbol: 'circle',
          lineWidth: 1,
          fillColor: '#777',
          lineColor: '#333',
        },
      };
      data.series.push(product);
      // Construct client zones
      data.series.push(this._generateClientZone(zones));
    }

    return data;
  }

  /**
   * Generate the data for each map.
   * @private
   */
  _generateData() {
    const maps = this.configuration.maps || [];

    for (const map of maps) {
      const data = this._generateSeries(map, this.configuration.client);
      this.data[map.id] = {
        id: map.id,
        name: map.name,
        productEligibilityAdvisedValue: map.productEligibilityAdvisedValue,
        productEligibilityNonAdvisedValue: map.productEligibilityNonAdvisedValue,
        productTargetMarketAdvised: map.productTargetMarketAdvised,
        productTargetMarketNonAdvised: map.productTargetMarketNonAdvised,
        maximum: data?.maximum,
        series: data?.series,
      };
    }
  }

  /**
   * Generate the chart for one specific set of data (i.e. for one map).
   * @param data
   * @private
   */
  _generateChart(data: Data) {
    window.Highcharts.chart(`mifid2-container-${this.configuration.container}`, {
      legend: {
        itemStyle: {
          cursor: 'default',
        },
      },
      lang: {
        locale: 'en',
      },
      chart: {
        styledMode: true,
        type: 'polygon',
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        min: 0,
        max: data.maximum?.complexity,
        tickInterval: 10,
        title: {
          text: 'Complexity',
        },
      },
      yAxis: {
        min: 0,
        max: data.maximum?.risk,
        tickInterval: 10,
        gridLineWidth: 0,
        lineWidth: 1,
        tickWidth: 1,
        title: {
          text: 'Risk',
        },
      },
      tooltip: {
        split: false,
        borderColor: '#ccc',
        formatter: function () {
          const options = this.series.userOptions as SeriesPolygonOptions | SeriesLineOptions;
          const optionsData = options.data as [string | number, number | null][];
          if (this.series.name === 'Product') {
            return (
              '<b>' +
              this.series.name +
              '</b><br>' +
              '<b>Complexity : </b>' +
              optionsData[0][0] +
              '<br>' +
              '<b>Risk : </b>' +
              optionsData[0][1]
            );
          } else if (this.series.name !== 'Client zone') {
            return (
              '<b>' +
              this.series.name +
              '</b><br>' +
              '<b>Complexity : </b>' +
              optionsData[2][0] +
              '<br>' +
              '<b>Complexity Risk 0 : </b>' +
              optionsData[3][0] +
              '<br>' +
              '<b>Risk : </b>' +
              optionsData[1][1]
            );
          }
          return '<b>' + this.series.name + '</b>';
        },
      },
      series: data.series,
    });
  }
}
